import React, { useCallback, useEffect, useState } from 'react'
import { Eye, History, RefreshCw, Save, Send } from 'lucide-react'
import { toast } from 'react-toastify'
import emailNotificationService, { describeError } from '../../services/emailNotificationService'

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly_calendar_day', label: 'Monthly (calendar day)' },
  { value: 'monthly_last_business_day', label: 'Monthly (last business day)' },
  { value: 'monthly_nth_business_day', label: 'Monthly (Nth business day)' },
  { value: 'event_driven', label: 'Event-driven (lead days)' },
]

const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

const WEEKEND_POLICIES = [
  { value: 'calculated', label: 'Run on the same calendar day' },
  { value: 'previous_working', label: 'Move to previous business day' },
  { value: 'next_working', label: 'Move to next business day' },
  { value: 'skip', label: 'Skip that occurrence' },
]

const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'America/New_York',
  'UTC',
]

function formatWhen(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
}

function reportError(err, fallback) {
  const { message, handled } = describeError(err, fallback)
  if (!handled) toast.error(message)
}

/**
 * Per-event Management MIS schedule, thresholds, content, preview, send-now, history.
 * Recipients (To/CC/BCC) remain on the parent EmailNotifications panel.
 */
export default function ManagementEmailSettingsPanel({
  eventKey,
  canEdit,
  users = [],
}) {
  const [cfg, setCfg] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [sending, setSending] = useState(false)
  const [preview, setPreview] = useState(null)
  const [dirty, setDirty] = useState(false)

  const load = useCallback(async () => {
    if (!eventKey) return
    setLoading(true)
    try {
      const [cfgRes, histRes] = await Promise.all([
        emailNotificationService.getManagementConfig(eventKey),
        emailNotificationService.managementHistory(eventKey, { limit: 20 }).catch(() => ({ data: [] })),
      ])
      setCfg(cfgRes?.data || null)
      setHistory(Array.isArray(histRes?.data) ? histRes.data : [])
      setDirty(false)
      setPreview(null)
    } catch (err) {
      reportError(err, 'Failed to load Management email settings')
      setCfg(null)
    } finally {
      setLoading(false)
    }
  }, [eventKey])

  useEffect(() => { load() }, [load])

  const patch = (partial) => {
    setCfg((c) => (c ? { ...c, ...partial } : c))
    setDirty(true)
  }

  const patchThreshold = (key, value) => {
    setCfg((c) => {
      if (!c) return c
      return { ...c, thresholds: { ...(c.thresholds || {}), [key]: value === '' ? undefined : Number(value) } }
    })
    setDirty(true)
  }

  const handleSave = async () => {
    if (!canEdit || !cfg) return
    setSaving(true)
    try {
      const res = await emailNotificationService.saveManagementConfig(eventKey, {
        enabled: cfg.enabled,
        frequency: cfg.frequency,
        dayOfWeek: cfg.dayOfWeek,
        calendarDay: cfg.calendarDay,
        businessDayOfMonth: cfg.businessDayOfMonth,
        sendTime: cfg.sendTime,
        timezone: cfg.timezone,
        weekendHolidayPolicy: cfg.weekendHolidayPolicy,
        sendOnlyWhenExceptions: cfg.sendOnlyWhenExceptions,
        sendWhenNoActivity: cfg.sendWhenNoActivity,
        subjectTemplate: cfg.subjectTemplate,
        introMessage: cfg.introMessage,
        sections: cfg.sections,
        attachPdf: cfg.attachPdf,
        attachSpreadsheet: cfg.attachSpreadsheet,
        thresholds: cfg.thresholds,
        renewalLeadDays: cfg.renewalLeadDays,
        escalationEmails: cfg.escalationEmails,
        escalationUserIds: cfg.escalationUserIds,
      })
      setCfg(res?.data || cfg)
      setDirty(false)
      toast.success(res?.message || 'Management email settings saved')
    } catch (err) {
      reportError(err, 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handlePreview = async () => {
    if (!canEdit) return
    setPreviewing(true)
    try {
      const res = await emailNotificationService.previewManagement(eventKey)
      setPreview(res?.data || null)
      toast.success('Preview generated')
      emailNotificationService.managementHistory(eventKey, { limit: 20 })
        .then((r) => setHistory(Array.isArray(r?.data) ? r.data : []))
        .catch(() => {})
    } catch (err) {
      reportError(err, 'Preview failed')
    } finally {
      setPreviewing(false)
    }
  }

  const handleSendNow = async () => {
    if (!canEdit) return
    if (dirty) {
      toast.error('Save schedule settings first')
      return
    }
    setSending(true)
    try {
      const res = await emailNotificationService.sendManagementNow(eventKey)
      toast.success(res?.message || 'Email sent')
      load()
    } catch (err) {
      reportError(err, 'Send now failed')
    } finally {
      setSending(false)
    }
  }

  if (loading) return <div className="en-mgmt-loading">Loading Management email settings…</div>
  if (!cfg) return <div className="en-mgmt-loading">Could not load settings for this event.</div>

  const showWeekly = cfg.frequency === 'weekly'
  const showCalendarDay = cfg.frequency === 'monthly_calendar_day'
  const showNthBiz = cfg.frequency === 'monthly_nth_business_day'
  const showLeadDays = cfg.frequency === 'event_driven' || eventKey === 'management.renewal_commitment_alert'
  const showCriticalThresholds = eventKey === 'management.critical_finance_alert'

  return (
    <section className="en-mgmt">
      <header className="en-mgmt__head">
        <div>
          <h3>Schedule &amp; content</h3>
          <p>Independent of other Management emails. Changing these settings does not affect other events.</p>
        </div>
        <div className="en-mgmt__meta">
          <span>Last sent: <strong>{formatWhen(cfg.lastSentAt)}</strong></span>
          <span>Next scheduled: <strong>{formatWhen(cfg.nextScheduledAt)}</strong></span>
        </div>
      </header>

      <p className="en-callout">
        {cfg.holidayNote || 'Holiday calendar not configured — weekend adjustment only (Sat/Sun).'}
      </p>

      <div className="en-mgmt-grid">
        <label className="en-field">
          <span>Frequency</span>
          <select
            className="en-input"
            disabled={!canEdit}
            value={cfg.frequency || 'daily'}
            onChange={(e) => patch({ frequency: e.target.value })}
          >
            {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>

        {showWeekly && (
          <label className="en-field">
            <span>Day of week</span>
            <select
              className="en-input"
              disabled={!canEdit}
              value={cfg.dayOfWeek ?? 1}
              onChange={(e) => patch({ dayOfWeek: Number(e.target.value) })}
            >
              {WEEKDAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </label>
        )}

        {showCalendarDay && (
          <label className="en-field">
            <span>Calendar day</span>
            <input
              className="en-input"
              type="number"
              min={1}
              max={31}
              disabled={!canEdit}
              value={cfg.calendarDay ?? 15}
              onChange={(e) => patch({ calendarDay: Number(e.target.value) })}
            />
          </label>
        )}

        {showNthBiz && (
          <label className="en-field">
            <span>Business day of month</span>
            <input
              className="en-input"
              type="number"
              min={1}
              max={22}
              disabled={!canEdit}
              value={cfg.businessDayOfMonth ?? 5}
              onChange={(e) => patch({ businessDayOfMonth: Number(e.target.value) })}
            />
          </label>
        )}

        <label className="en-field">
          <span>Send time</span>
          <input
            className="en-input"
            type="time"
            disabled={!canEdit}
            value={cfg.sendTime || '09:00'}
            onChange={(e) => patch({ sendTime: e.target.value })}
          />
        </label>

        <label className="en-field">
          <span>Timezone</span>
          <select
            className="en-input"
            disabled={!canEdit}
            value={cfg.timezone || 'Asia/Kolkata'}
            onChange={(e) => patch({ timezone: e.target.value })}
          >
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </label>

        <label className="en-field">
          <span>Weekend / holiday adjustment</span>
          <select
            className="en-input"
            disabled={!canEdit}
            value={cfg.weekendHolidayPolicy || 'calculated'}
            onChange={(e) => patch({ weekendHolidayPolicy: e.target.value })}
          >
            {WEEKEND_POLICIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
      </div>

      <div className="en-mgmt-checks">
        <label>
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={Boolean(cfg.sendOnlyWhenExceptions)}
            onChange={(e) => patch({ sendOnlyWhenExceptions: e.target.checked })}
          />
          Send only when exceptions exist
        </label>
        <label>
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={cfg.sendWhenNoActivity !== false}
            onChange={(e) => patch({ sendWhenNoActivity: e.target.checked })}
          />
          Send even when there is no activity
        </label>
        <label>
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={Boolean(cfg.attachPdf)}
            onChange={(e) => patch({ attachPdf: e.target.checked })}
          />
          Prefer PDF attachment
        </label>
        <label>
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={Boolean(cfg.attachSpreadsheet)}
            onChange={(e) => patch({ attachSpreadsheet: e.target.checked })}
          />
          Prefer spreadsheet attachment
        </label>
      </div>

      {showLeadDays && (
        <label className="en-field en-field--wide">
          <span>Renewal lead days (comma-separated)</span>
          <input
            className="en-input"
            disabled={!canEdit}
            value={(cfg.renewalLeadDays || []).join(', ')}
            onChange={(e) => patch({
              renewalLeadDays: e.target.value.split(/[,\s]+/).map(Number).filter((n) => Number.isFinite(n)),
            })}
            placeholder="90, 60, 30, 15, 7, 1"
          />
        </label>
      )}

      {showCriticalThresholds && (
        <div className="en-mgmt-thresholds">
          <h4>Financial thresholds</h4>
          <div className="en-mgmt-grid">
            {[
              ['minCash', 'Minimum cash (₹)'],
              ['minRunwayMonths', 'Minimum runway (months)'],
              ['overdueDays', 'Material overdue days'],
              ['materialOverdueAmount', 'Material overdue amount (₹)'],
              ['budgetOverspendPct', 'Budget overspend %'],
              ['expenseSpikePct', 'Expense spike %'],
              ['renewalDays', 'Renewal decision window (days)'],
              ['escalationIntervalDays', 'Re-alert interval (days)'],
            ].map(([key, label]) => (
              <label key={key} className="en-field">
                <span>{label}</span>
                <input
                  className="en-input"
                  type="number"
                  disabled={!canEdit}
                  value={cfg.thresholds?.[key] ?? ''}
                  onChange={(e) => patchThreshold(key, e.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      <label className="en-field en-field--wide">
        <span>Subject template</span>
        <input
          className="en-input"
          disabled={!canEdit}
          value={cfg.subjectTemplate || ''}
          onChange={(e) => patch({ subjectTemplate: e.target.value })}
        />
      </label>

      <label className="en-field en-field--wide">
        <span>Introductory message</span>
        <textarea
          className="en-input"
          rows={2}
          disabled={!canEdit}
          value={cfg.introMessage || ''}
          onChange={(e) => patch({ introMessage: e.target.value })}
        />
      </label>

      <label className="en-field en-field--wide">
        <span>Escalation emails (comma-separated)</span>
        <input
          className="en-input"
          disabled={!canEdit}
          value={(cfg.escalationEmails || []).join(', ')}
          onChange={(e) => patch({
            escalationEmails: e.target.value.split(/[,\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean),
          })}
          placeholder="ceo@company.com"
        />
      </label>

      <div className="en-actions en-mgmt-actions">
        <button type="button" className="en-btn en-btn--primary" disabled={!canEdit || saving || !dirty} onClick={handleSave}>
          <Save size={16} aria-hidden />
          {saving ? 'Saving…' : 'Save schedule'}
        </button>
        <button type="button" className="en-btn en-btn--secondary" disabled={!canEdit || previewing} onClick={handlePreview}>
          <Eye size={16} aria-hidden />
          {previewing ? 'Previewing…' : 'Preview'}
        </button>
        <button type="button" className="en-btn en-btn--secondary" disabled={!canEdit || sending || dirty || !cfg.enabled} onClick={handleSendNow}>
          <Send size={16} aria-hidden />
          {sending ? 'Sending…' : 'Send now'}
        </button>
        <button type="button" className="en-btn en-btn--ghost" disabled={loading} onClick={load}>
          <RefreshCw size={16} aria-hidden />
          Refresh
        </button>
        {dirty ? <span className="en-actions__dirty">Unsaved schedule changes</span> : null}
      </div>

      {preview && (
        <div className="en-mgmt-preview">
          <h4>Preview</h4>
          <p><strong>Subject:</strong> {preview.subject}</p>
          <p><strong>Period:</strong> {preview.reportingPeriod?.label || '—'}</p>
          <p><strong>To:</strong> {(preview.to || []).join(', ') || '—'}</p>
          <p><strong>CC:</strong> {(preview.cc || []).join(', ') || '—'}</p>
          <p><strong>BCC:</strong> {(preview.bcc || []).join(', ') || '—'}</p>
          {(preview.warnings || []).length ? (
            <p className="en-mgmt-preview__warn">Warnings: {preview.warnings.join('; ')}</p>
          ) : null}
          {preview.skipReason ? (
            <p className="en-mgmt-preview__warn">Would skip on schedule: {preview.skipReason}</p>
          ) : null}
          <div className="en-mgmt-preview__frame" dangerouslySetInnerHTML={{ __html: preview.html || '' }} />
        </div>
      )}

      <div className="en-mgmt-history">
        <h4><History size={14} aria-hidden /> Delivery history</h4>
        {!history.length ? (
          <p className="en-mgmt-history__empty">No sends yet for this event.</p>
        ) : (
          <table className="en-mgmt-history__table">
            <thead>
              <tr>
                <th>When</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Period</th>
                <th>Subject</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h._id}>
                  <td>{formatWhen(h.createdAt || h.generatedAt)}</td>
                  <td>{h.triggerType}{h.isTest ? ' (test)' : ''}</td>
                  <td>{h.deliveryStatus}</td>
                  <td>{h.reportingPeriod?.label || '—'}</td>
                  <td>{h.subject || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}
